import { IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { FC, useEffect, useState } from 'react';
import { WbTooltip } from '../WbTooltip';
import { WbUploaderFileFormat } from './formats';

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: `${theme.spacing(0.5)}px 0`,
  },
  thumbnail: {
    width: 128,
    height: 96,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    objectFit: 'cover',
    flexShrink: 0,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImg: {
    width: 128,
    height: 96,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    objectFit: 'cover',
    flexShrink: 0,
    display: 'block',
  },
  thumbnailPdf: {
    width: 128,
    height: 96,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    flexShrink: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  },
  fileInfo: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  filename: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

type ThumbnailProps = {
  file: File;
  format: WbUploaderFileFormat | undefined;
  classes: ReturnType<typeof useStyles>;
};

const FileThumbnail: FC<ThumbnailProps> = ({ file, format, classes }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!format?.previewable) return undefined;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file, format]);

  if (!format?.previewable || !objectUrl) {
    return (
      <div className={classes.thumbnail}>
        <InsertDriveFileIcon color="action" />
      </div>
    );
  }

  if (file.type === 'application/pdf') {
    return (
      <div className={classes.thumbnailPdf}>
        <PictureAsPdfIcon color="action" fontSize="large" />
      </div>
    );
  }

  return (
    <img src={objectUrl} alt={file.name} className={classes.thumbnailImg} />
  );
};

export type WbUploaderFilePreviewProps = {
  file: File;
  format: WbUploaderFileFormat | undefined;
  onDownload?: (file: File) => void;
  onDelete: (file: File) => void;
  disabled?: boolean;
};

export const WbUploaderFilePreview: FC<WbUploaderFilePreviewProps> = ({
  file,
  format,
  onDownload,
  onDelete,
  disabled = false,
}) => {
  const classes = useStyles();

  const handleOpenPreview = () => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const previewText = `Open preview of ${file.name}`;

  const downloadText = `Download ${file.name}`;

  const deleteText = `Remove ${file.name}`;

  return (
    <div className={classes.row} role="listitem" aria-label={file.name}>
      <FileThumbnail file={file} format={format} classes={classes} />
      <div className={classes.fileInfo}>
        <AttachFileIcon fontSize="small" color="action" />
        <WbTooltip title={file.name}>
          <Typography variant="body2" className={classes.filename}>
            {file.name}
          </Typography>
        </WbTooltip>
      </div>
      <div className={classes.actions}>
        {format?.previewable && (
          <WbTooltip title={previewText}>
            <span>
              <IconButton
                size="small"
                onClick={handleOpenPreview}
                disabled={disabled}
                aria-label={previewText}
              >
                <OpenInNewIcon />
              </IconButton>
            </span>
          </WbTooltip>
        )}
        {onDownload && (
          <WbTooltip title={downloadText}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDownload(file)}
                disabled={disabled}
                aria-label={downloadText}
              >
                <GetAppIcon />
              </IconButton>
            </span>
          </WbTooltip>
        )}
        <WbTooltip title={deleteText}>
          <span>
            <IconButton
              size="small"
              onClick={() => onDelete(file)}
              disabled={disabled}
              aria-label={deleteText}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </WbTooltip>
      </div>
    </div>
  );
};
